/* ==========================================================================
   Vision Neo — interactions for the redesigned chrome and sections.
   Theme toggle, sticky header state, mobile drawer, scroll reveal, tabs.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;

  /* ---- Theme toggle ---- */
  // Shopify's Apple Pay button is black (verified 2026-10-13, device in dark mode too).
  // Flip it to Apple's white style only in the site's dark mode.
  function syncApplePayTone() {
    root.classList.toggle('vn-ap-invert', root.getAttribute('data-theme') === 'dark');
  }
  syncApplePayTone();

  // Colour transitions stay off for the swap frame (they used to fade one by one, which looked slow and messy —
  // user feedback 2026-10-14). Since 2026-10-15 the new theme is revealed as a circle growing from the toggle
  // (View Transitions API: one GPU snapshot, ~0.5s). Browsers without it, and reduced motion, stay instant.
  var reduceMotion = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };

  function setTheme(theme, origin) {
    if (!document.startViewTransition || reduceMotion.matches || document.hidden) { applyTheme(theme); return; }
    var x = window.innerWidth / 2, y = 0;
    if (origin && origin.getBoundingClientRect) {
      var r = origin.getBoundingClientRect();
      x = r.left + r.width / 2; y = r.top + r.height / 2;
    }
    var radius = Math.hypot(Math.max(x, window.innerWidth - x), Math.max(y, window.innerHeight - y));
    // 2026-10-16 (the switch felt laggy): the "no transitions" class goes on with the snapshot and comes off only
    // after the circle has finished, so the whole page is restyled once, not again in the middle of the animation
    root.classList.add('vn-vt', 'vn-theme-swap');
    var vt = document.startViewTransition(function () { applyTheme(theme, true); });
    vt.ready.then(function () {
      root.animate(
        { clipPath: ['circle(0px at ' + x + 'px ' + y + 'px)', 'circle(' + radius + 'px at ' + x + 'px ' + y + 'px)'] },
        { duration: 450, easing: 'cubic-bezier(.22, 1, .36, 1)', pseudoElement: '::view-transition-new(root)' }
      );
    }).catch(function () {});
    vt.finished.finally(function () { root.classList.remove('vn-vt', 'vn-theme-swap'); });
  }

  function applyTheme(theme, inTransition) {
    if (!inTransition) root.classList.add('vn-theme-swap');
    root.setAttribute('data-theme', theme);
    if (!inTransition) {
      void root.offsetWidth;
      requestAnimationFrame(function () { requestAnimationFrame(function () { root.classList.remove('vn-theme-swap'); }); });
    }
    syncApplePayTone();
    try { localStorage.setItem('vn_theme', theme); } catch (e) {}
    var meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#07060f' : '#f5f4fb');
    document.querySelectorAll('[data-vn-theme-toggle]').forEach(function (btn) {
      btn.setAttribute('aria-pressed', String(theme === 'dark'));
    });
  }

  function initThemeToggle() {
    document.querySelectorAll('[data-vn-theme-toggle]').forEach(function (btn) {
      if (btn.dataset.vnBound) return;
      btn.dataset.vnBound = '1';
      btn.setAttribute('aria-pressed', String(root.getAttribute('data-theme') === 'dark'));
      btn.addEventListener('click', function () {
        btn.classList.remove('is-flip'); void btn.offsetWidth; btn.classList.add('is-flip');
        setTheme(root.getAttribute('data-theme') === 'dark' ? 'light' : 'dark', btn);
      });
    });
  }

  /* ---- Header scroll state + offset variable ---- */
  function initHeader() {
    var header = document.querySelector('[data-vn-header]');
    if (!header || header.dataset.vnBound) return;
    header.dataset.vnBound = '1';

    // Preview helper: ?vn_header=bar|island|a|b|c swaps the header style without touching settings
    // (kept for the visit in sessionStorage; ?vn_header=reset clears it). a/b/c also hide the announcement bar.
    var STYLES = ['bar', 'island', 'a', 'b', 'c', 'd', 'e'];
    var forced = new URLSearchParams(location.search).get('vn_header');
    try {
      if (forced === 'reset') { sessionStorage.removeItem('vn_header'); forced = null; }
      else if (STYLES.indexOf(forced) > -1) sessionStorage.setItem('vn_header', forced);
      else forced = sessionStorage.getItem('vn_header');
    } catch (e) {}
    var bar = header.querySelector('.vn-header');
    if (bar && bar.classList.contains('vn-header--pro')) forced = null; // the pro header has its own markup
    if (bar && STYLES.indexOf(forced) > -1) {
      STYLES.forEach(function (st) { bar.classList.remove('vn-header--' + st); });
      bar.classList.add('vn-header--' + forced);
      var ann = document.getElementById('shopify-section-vs-announcement-bar');
      if (ann) ann.style.display = (forced === 'a' || forced === 'b' || forced === 'c') ? 'none' : '';
    }

    // <html> gets vn-hs-<style> so things outside the header (the style e tab bar) can follow the style.
    var curStyle = STYLES.filter(function (st) { return bar && bar.classList.contains('vn-header--' + st); })[0];
    if (curStyle) root.classList.add('vn-hs-' + curStyle);
    initSegNav(bar);
    initTypeHint(bar);

    var annSec = document.getElementById('shopify-section-vs-announcement-bar');

    // Search icon (styles b/c/d): opens a search panel under the header.
    var sToggle = header.querySelector('[data-vn-search-toggle]');
    var sPanel = header.querySelector('[data-vn-search-panel]');
    if (sToggle && sPanel) {
      var setSearch = function (open) {
        bar.classList.toggle('is-search-open', open);
        sToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (open) { var inp = sPanel.querySelector('input[type="search"]'); if (inp) setTimeout(function () { inp.focus(); }, 60); }
        root.style.setProperty('--vn-header-offset', (header.offsetHeight + (annSec && annSec.offsetParent ? annSec.offsetHeight : 0)) + 'px');
      };
      sToggle.addEventListener('click', function () { setSearch(!bar.classList.contains('is-search-open')); });
      // style e: the tab bar's search button opens the same panel and scrolls up to it
      document.querySelectorAll('[data-vn-tab-search]').forEach(function (b) {
        b.addEventListener('click', function (e) { e.stopPropagation(); window.scrollTo({ top: 0, behavior: 'smooth' }); setSearch(true); });
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && bar.classList.contains('is-search-open')) { setSearch(false); sToggle.focus(); } });
      document.addEventListener('click', function (e) { if (bar.classList.contains('is-search-open') && !header.contains(e.target)) setSearch(false); });
    }
    var ticking = false;

    var scrolled = false;
    // 2026-10-16: a custom property written on <html> restyles every element on the page, so it is written only when
    // its value really changes (it used to be rewritten on every scroll frame, which made scrolling stutter)
    var varCache = {};
    function setRootVar(name, value) {
      if (varCache[name] === value) return;
      varCache[name] = value;
      root.style.setProperty(name, value);
    }
    function update() {
      // Hysteresis so the state can't flip back and forth around one scroll position.
      if (!scrolled && window.scrollY > 40) scrolled = true;
      else if (scrolled && window.scrollY < 8) scrolled = false;
      if (root.classList.contains('is-scrolled') !== scrolled) root.classList.toggle('is-scrolled', scrolled);
      var headerH = header.offsetHeight;
      setRootVar('--vs-header-offset', headerH + 'px');
      // the sticky announcement bar sits above the header, so sticky things below must clear both
      var annH = annSec && annSec.offsetParent ? annSec.offsetHeight : 0;
      setRootVar('--vn-ann-h', annH + 'px');
      setRootVar('--vn-header-offset', (headerH + annH) + 'px');
      ticking = false;
    }
    requestAnimationFrame(update); // first measure one frame later, not while the script is still evaluating
    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });
    window.addEventListener('resize', update);
  }

  // Style e: a sliding pill under the nav links that follows the hovered link and rests on the current page.
  function initSegNav(bar) {
    var ul = bar && bar.querySelector('.vn-header__nav ul');
    if (!ul || ul.querySelector('.vn-seg')) return;
    var pill = document.createElement('li');
    pill.className = 'vn-seg';
    pill.setAttribute('aria-hidden', 'true');
    ul.appendChild(pill);
    var links = ul.querySelectorAll('a');
    var home = ul.querySelector('a[aria-current="page"]') || links[0];
    function moveTo(a) {
      if (!a || !bar.classList.contains('vn-header--e')) return;
      var r = a.getBoundingClientRect(), u = ul.getBoundingClientRect();
      pill.style.width = r.width + 'px';
      pill.style.transform = 'translateX(' + (r.left - u.left) + 'px)';
      pill.style.opacity = '1';
    }
    links.forEach(function (a) { a.addEventListener('mouseenter', function () { moveTo(a); }); a.addEventListener('focus', function () { moveTo(a); }); });
    ul.addEventListener('mouseleave', function () { moveTo(home); });
    window.addEventListener('resize', function () { moveTo(home); });
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(function () { moveTo(home); });
    setTimeout(function () { moveTo(home); }, 50);
  }

  // Styles d/e: the search placeholder types out brand names one after another.
  function initTypeHint(bar) {
    if (!bar || !(bar.classList.contains('vn-header--d') || bar.classList.contains('vn-header--e'))) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    var inputs = bar.querySelectorAll('input[type="search"]');
    var words = ['شات جي بي تي', 'أدوبي فوتوشوب', 'أوفيس 365', 'كانفا برو', 'جيم باس ألتميت', 'جيمناي'];
    var wi = 0, ci = 0, del = false;
    function step() {
      var w = words[wi];
      ci += del ? -1 : 1;
      var text = 'ابحث عن ' + w.slice(0, ci) + (del ? '' : '|');
      inputs.forEach(function (inp) { if (!inp.value) inp.placeholder = text; });
      var wait = del ? 40 : 90;
      if (!del && ci === w.length) { del = true; wait = 1600; }
      else if (del && ci === 0) { del = false; wi = (wi + 1) % words.length; wait = 300; }
      setTimeout(step, wait);
    }
    step();
  }

  /* ---- Pro header (snippets/vn-header-pro): categories panel, search palette ---- */
  function initProHeader() {
    var wrap = document.querySelector('[data-vnp-wrap]');
    if (!wrap || wrap.dataset.vnpBound) return;
    wrap.dataset.vnpBound = '1';
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var desk = window.matchMedia('(min-width: 1024px)');

    /* greeting by time of day (used in the palette and the phone menu) */
    var pal = document.querySelector('[data-vnp-pal]');
    var name = pal ? (pal.getAttribute('data-name') || '').trim() : '';
    var hr = new Date().getHours();
    var hi = hr >= 4 && hr < 12 ? 'صباح الخير' : 'مساء الخير';
    hi += name ? '، ' + name + ' 👋' : (hr >= 4 && hr < 12 ? ' ☀️' : ' 🌙');
    document.querySelectorAll('[data-vnp-hi]').forEach(function (el) { el.textContent = hi; });

    /* «الأقسام» button (desktop): opens a panel; hovering/focusing a category on the side shows its brands + top products */
    var catsBtn = wrap.querySelector('[data-vnp-cats-btn]');
    var cats = wrap.querySelector('[data-vnp-cats]');
    function catsOpen() { return !!cats && cats.classList.contains('is-open'); }
    function setCats(open, refocus) {
      if (!cats || !catsBtn || open === catsOpen()) return;
      cats.classList.toggle('is-open', open);
      catsBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
      root.classList.toggle('vnp-mega-open', open);
      if (!open && refocus) catsBtn.focus();
    }
    if (cats && catsBtn) {
      var tabs = Array.prototype.slice.call(cats.querySelectorAll('[data-vnp-tab]'));
      var panes = Array.prototype.slice.call(cats.querySelectorAll('[data-vnp-pane]'));
      var showPane = function (i) {
        tabs.forEach(function (t) { t.classList.toggle('is-active', t.getAttribute('data-vnp-tab') === i); });
        panes.forEach(function (p) { p.classList.toggle('is-active', p.getAttribute('data-vnp-pane') === i); });
      };
      tabs.forEach(function (t) {
        var i = t.getAttribute('data-vnp-tab');
        t.addEventListener('mouseenter', function () { showPane(i); });
        t.addEventListener('focus', function () { showPane(i); });
      });
      catsBtn.addEventListener('click', function (e) { e.stopPropagation(); setCats(!catsOpen()); });
      document.addEventListener('click', function (e) { if (catsOpen() && !cats.contains(e.target)) setCats(false); });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape' && catsOpen()) setCats(false, true); });
      cats.addEventListener('focusout', function (e) { if (catsOpen() && e.relatedTarget && !cats.contains(e.relatedTarget) && e.relatedTarget !== catsBtn) setCats(false); });
      desk.addEventListener && desk.addEventListener('change', function () { setCats(false); });
      /* the panel's brand tiles and product thumbs were lazy, so they only started loading when it opened (felt like a lag):
         load them once the page is idle, or as soon as the pointer reaches the button */
      var warm = function () {
        if (warm.done) return; warm.done = true;
        cats.querySelectorAll('img[loading="lazy"]').forEach(function (im) { im.loading = 'eager'; });
      };
      catsBtn.addEventListener('pointerenter', warm);
      catsBtn.addEventListener('focus', warm);
      var idle = window.requestIdleCallback || function (f) { return setTimeout(f, 1500); };
      if (document.readyState === 'complete') idle(warm, { timeout: 3000 });
      else window.addEventListener('load', function () { idle(warm, { timeout: 3000 }); });
    }

    /* the search pill cycles through brand names */
    var rot = wrap.querySelector('[data-vnp-rot]');
    if (rot && !reduce) {
      var words = ['شات جي بي تي', 'أدوبي فوتوشوب', 'أوفيس 365', 'كانفا برو', 'جيم باس', 'جيمناي', 'ويندوز 11'];
      var wi = 0;
      setInterval(function () {
        if (document.hidden) return;
        rot.classList.add('is-out');
        setTimeout(function () {
          wi = (wi + 1) % words.length;
          rot.textContent = words[wi];
          rot.classList.remove('is-out');
          rot.classList.add('is-in');
          void rot.offsetWidth;
          rot.classList.remove('is-in');
        }, 260);
      }, 2800);
    }

    // The header stays fixed on phones too (no hide on scroll, user request 2026-10-14).

    /* cart count: hide when 0, bump when it changes */
    var count = wrap.querySelector('[data-vnp-cart] .vs-header__cart-count');
    if (count && window.MutationObserver) {
      var prev = count.textContent.trim();
      new MutationObserver(function () {
        var v = count.textContent.trim();
        count.classList.toggle('is-zero', v === '0' || v === '');
        if (v !== prev && !reduce) {
          var cart = count.parentNode;
          cart.classList.remove('is-bump'); void cart.offsetWidth; cart.classList.add('is-bump');
        }
        prev = v;
      }).observe(count, { childList: true, characterData: true, subtree: true });
    }

    if (!pal) return;
    /* ---------- search palette ---------- */
    var box = pal.querySelector('.vnp-pal__box');
    var form = pal.querySelector('[data-vnp-form]');
    var input = pal.querySelector('[data-vnp-input]');
    var body = pal.querySelector('[data-vnp-body]');
    var home = pal.querySelector('[data-vnp-home]');
    var results = pal.querySelector('[data-vnp-results]');
    var spin = pal.querySelector('[data-vnp-spin]');
    var clearBtn = pal.querySelector('[data-vnp-clear]');
    var recentWrap = pal.querySelector('[data-vnp-recent-wrap]');
    var recentBox = pal.querySelector('[data-vnp-recent]');
    var suggestUrl = pal.getAttribute('data-suggest-url') || '/search/suggest';
    var wa = pal.getAttribute('data-wa') || '';
    var lastFocus = null, tHide = 0, tFetch = 0, ctrl = null, active = -1, cache = {};

    function getRecent() { try { return JSON.parse(localStorage.getItem('vnp_recent') || '[]').slice(0, 6); } catch (e) { return []; } }
    function saveRecent(q) {
      q = (q || '').trim();
      if (q.length < 2) return;
      var list = getRecent().filter(function (x) { return x !== q; });
      list.unshift(q);
      try { localStorage.setItem('vnp_recent', JSON.stringify(list.slice(0, 6))); } catch (e) {}
    }
    function drawRecent() {
      var list = getRecent();
      recentWrap.hidden = !list.length;
      recentBox.innerHTML = '';
      list.forEach(function (q) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'vnp-chip vnp-chip--recent';
        b.setAttribute('data-vnp-item', '');
        b.textContent = q;
        b.addEventListener('click', function () { input.value = q; onInput(); input.focus(); });
        recentBox.appendChild(b);
      });
    }
    pal.querySelector('[data-vnp-recent-clear]').addEventListener('click', function () {
      try { localStorage.removeItem('vnp_recent'); } catch (e) {}
      drawRecent();
      input.focus();
    });

    function visibleItems() {
      var scope = results.hidden ? home : results;
      return Array.prototype.slice.call(scope.querySelectorAll('[data-vnp-item]')).filter(function (el) { return el.offsetParent !== null; });
    }
    function setActive(i) {
      var list = visibleItems();
      list.forEach(function (el) { el.classList.remove('is-active'); });
      if (!list.length) { active = -1; return; }
      active = (i + list.length) % list.length;
      list[active].classList.add('is-active');
      list[active].scrollIntoView({ block: 'nearest' });
    }

    function open() {
      clearTimeout(tHide);
      if (!pal.hidden && pal.classList.contains('is-open')) { input.focus(); return; }
      lastFocus = document.activeElement;
      setCats(false);
      drawRecent();
      pal.hidden = false;
      root.classList.add('vnp-lock');
      requestAnimationFrame(function () { pal.classList.add('is-open'); });
      // after other click handlers (e.g. the phone menu closing and refocusing its button)
      setTimeout(function () { input.focus(); }, 60);
    }
    function close() {
      if (pal.hidden) return;
      pal.classList.remove('is-open');
      root.classList.remove('vnp-lock');
      tHide = setTimeout(function () { pal.hidden = true; }, reduce ? 0 : 220);
      if (lastFocus && lastFocus.focus && document.contains(lastFocus)) lastFocus.focus();
    }
    document.querySelectorAll('[data-vnp-open]').forEach(function (b) { b.addEventListener('click', open); });
    pal.querySelectorAll('[data-vnp-close]').forEach(function (b) { b.addEventListener('click', close); });

    document.addEventListener('keydown', function (e) {
      var t = e.target;
      var typing = t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName));
      if ((e.ctrlKey || e.metaKey) && (e.key === 'k' || e.key === 'K')) { e.preventDefault(); if (pal.hidden) open(); else close(); return; }
      if (e.key === '/' && !typing && pal.hidden) { e.preventDefault(); open(); return; }
      if (pal.hidden) return;
      if (e.key === 'Escape') { e.preventDefault(); close(); return; }
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); return; }
      if (e.key === 'Enter' && active > -1) {
        var el = visibleItems()[active];
        if (el) { e.preventDefault(); if (input.value.trim()) saveRecent(input.value); el.click(); }
        return;
      }
      if (e.key === 'Tab') { // keep focus inside the palette
        var f = Array.prototype.slice.call(box.querySelectorAll('a[href], button:not([hidden]), input:not([type="hidden"])')).filter(function (x) { return x.offsetParent !== null; });
        if (!f.length) return;
        var i = f.indexOf(document.activeElement);
        if (e.shiftKey && i <= 0) { e.preventDefault(); f[f.length - 1].focus(); }
        else if (!e.shiftKey && i === f.length - 1) { e.preventDefault(); f[0].focus(); }
      }
    });

    function showHome() {
      results.hidden = true;
      results.innerHTML = '';
      home.hidden = false;
      input.setAttribute('aria-expanded', 'false');
      active = -1;
    }
    function render(html, q) {
      if (input.value.trim() !== q) return; // a newer query is on its way
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var res = doc.querySelector('[data-vnp-res]');
      if (!res) return;
      results.innerHTML = '';
      results.appendChild(res);
      var waLink = results.querySelector('[data-vnp-wa]');
      if (waLink) {
        waLink.href = 'https://wa.me/' + wa + '?text=' + encodeURIComponent('مرحبا، أبحث عن: ' + q);
        waLink.target = '_blank';
        waLink.rel = 'noopener';
      }
      results.querySelectorAll('a[data-vnp-item]').forEach(function (a) { a.addEventListener('click', function () { saveRecent(q); }); });
      home.hidden = true;
      results.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
      body.scrollTop = 0;
    }
    function onInput() {
      var q = input.value.trim();
      clearBtn.hidden = !input.value;
      clearTimeout(tFetch);
      if (ctrl) ctrl.abort();
      if (!q) { spin.classList.remove('is-on'); showHome(); return; }
      if (cache[q]) { render(cache[q], q); return; }
      tFetch = setTimeout(function () {
        spin.classList.add('is-on');
        ctrl = window.AbortController ? new AbortController() : null;
        var url = suggestUrl + '?q=' + encodeURIComponent(q) +
          '&resources[type]=product,collection&resources[limit]=6&resources[limit_scope]=each' +
          '&resources[options][unavailable_products]=last&section_id=vn-predictive-search';
        fetch(url, ctrl ? { signal: ctrl.signal } : {})
          .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
          .then(function (html) { cache[q] = html; render(html, q); })
          .catch(function (err) { if (err && err.name === 'AbortError') return; })
          .then(function () { if (input.value.trim() === q) spin.classList.remove('is-on'); });
      }, 180);
    }
    input.addEventListener('input', onInput);
    clearBtn.addEventListener('click', function () { input.value = ''; onInput(); input.focus(); });
    form.addEventListener('submit', function (e) {
      if (!input.value.trim()) { e.preventDefault(); return; }
      saveRecent(input.value);
    });
    pal.querySelectorAll('[data-vnp-home] a[data-vnp-item]').forEach(function (a) { a.addEventListener('click', close); });
  }

  /* ---- Announcement bar: one message at a time (sections/vs-announcement-bar) ---- */
  function initAnnounce() {
    var bar = document.querySelector('[data-vn-ann]');
    if (!bar || bar.dataset.vnBound) return;
    bar.dataset.vnBound = '1';
    var msgs = Array.prototype.slice.call(bar.querySelectorAll('[data-vn-ann-msg]'));
    if (msgs.length < 2) return;
    var ms = parseInt(bar.getAttribute('data-ms'), 10) || 4000;
    var cur = 0, timer = 0, paused = false;
    function show(i) {
      var next = (i + msgs.length) % msgs.length;
      if (next === cur) return;
      var old = msgs[cur];
      old.classList.remove('is-active');
      old.classList.add('is-leaving');
      old.setAttribute('aria-hidden', 'true');
      setTimeout(function () { old.classList.remove('is-leaving'); }, 520);
      cur = next;
      msgs[cur].classList.remove('is-leaving');
      msgs[cur].classList.add('is-active');
      msgs[cur].removeAttribute('aria-hidden');
      schedule();
    }
    function schedule() {
      clearTimeout(timer);
      if (!paused && !document.hidden) timer = setTimeout(function () { show(cur + 1); }, ms);
    }
    bar.addEventListener('mouseenter', function () { paused = true; clearTimeout(timer); });
    bar.addEventListener('mouseleave', function () { paused = false; schedule(); });
    bar.addEventListener('focusin', function () { paused = true; clearTimeout(timer); });
    bar.addEventListener('focusout', function (e) { if (!bar.contains(e.relatedTarget)) { paused = false; schedule(); } });
    document.addEventListener('visibilitychange', schedule);
    // swipe on phones (RTL: swiping left shows the next message)
    var x0 = null;
    bar.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
    bar.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      x0 = null;
      if (Math.abs(dx) > 30) show(cur + (dx < 0 ? 1 : -1));
    }, { passive: true });
    schedule();
  }

  /* ---- Mobile drawer ---- */
  function initDrawer() {
    var drawer = document.querySelector('[data-vn-drawer]');
    var overlay = document.querySelector('[data-vn-overlay]');
    if (!drawer || drawer.dataset.vnBound) return;
    drawer.dataset.vnBound = '1';
    var lastFocus = null;

    function open() {
      lastFocus = document.activeElement;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      var first = drawer.querySelector('button, a');
      if (first) first.focus();
    }
    function close() {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
      if (overlay) overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      if (lastFocus) lastFocus.focus();
    }
    document.querySelectorAll('[data-vn-drawer-open]').forEach(function (btn) {
      btn.addEventListener('click', open);
    });
    drawer.querySelectorAll('[data-vn-drawer-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });
    if (overlay) overlay.addEventListener('click', close);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.classList.contains('is-open')) close();
    });
  }

  /* ---- Scroll reveal ---- */
  function initReveal() {
    var items = document.querySelectorAll('[data-vn-reveal]:not(.is-in)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window)) {
      items.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var el = entry.target;
        el.classList.add('is-in');
        io.unobserve(el);
        // Auto-revealed cards drop the reveal rules afterwards so their own (faster) hover transitions apply again.
        if (el.dataset.vnAuto) setTimeout(function () { el.removeAttribute('data-vn-reveal'); el.classList.remove('is-in'); el.style.removeProperty('--vn-delay'); }, 1300);
      });
    }, { rootMargin: '0px 0px -40px 0px', threshold: 0 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---- Site-wide motion (2026-10-15): cards, headings and footer columns fade up as they scroll in ----
     Only elements still below the fold get hidden (no flash above it). Rects are read in one pass before any
     class is written; the animation itself is transform/opacity only. */
  var AUTO_REVEAL = '.vn-section .vn-head, .vn-row > *, .vn-grid > *, .vn-proof__item, .vn-footer .vn-container > *:not(.vn-footer__bottom)';
  function initAutoReveal() {
    if (reduceMotion.matches || !('IntersectionObserver' in window)) return;
    var vh = window.innerHeight;
    var picks = [];
    document.querySelectorAll(AUTO_REVEAL).forEach(function (el) {
      if (el.dataset.vnAuto || el.hasAttribute('data-vn-reveal') || (el.parentElement && el.parentElement.closest('[data-vn-reveal]:not(.is-in)'))) return;
      var r = el.getBoundingClientRect();
      if (r.height && r.top > vh) picks.push(el);
    });
    var lastParent = null, i = 0;
    picks.forEach(function (el) {
      el.dataset.vnAuto = '1';
      if (el.parentElement !== lastParent) { lastParent = el.parentElement; i = 0; }
      el.style.setProperty('--vn-delay', (Math.min(i++, 5) * 0.06) + 's');
      el.setAttribute('data-vn-reveal', '');
    });
  }

  /* ---- Count-up for the trust numbers ([data-vn-countup]: "+1,200", "99%", "5,000 عميل"; "24/7" stays) ---- */
  function initCountUp() {
    var els = document.querySelectorAll('[data-vn-countup]:not([data-vn-counted])');
    if (!els.length) return;
    var run = function (el) {
      el.setAttribute('data-vn-counted', '');
      var m = el.textContent.match(/^(\D*?)(\d[\d,]*)(.*)$/);
      if (!m || /\//.test(el.textContent) || reduceMotion.matches) return;
      var pre = m[1], post = m[3], target = parseInt(m[2].replace(/,/g, ''), 10), t0 = null;
      var step = function (t) {
        if (!t0) t0 = t;
        var p = Math.min((t - t0) / 1100, 1), eased = 1 - Math.pow(1 - p, 3);
        el.textContent = pre + Math.round(target * eased).toLocaleString('en-US') + post;
        if (p < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    };
    if (!('IntersectionObserver' in window)) return;
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        io.unobserve(entry.target);
        run(entry.target);
      });
    }, { threshold: 0.6 });
    els.forEach(function (el) { io.observe(el); });
  }

  /* ---- Banner tone trial (2026-10-15): ?vn_banner=theme|image, see the CSS block of the same name ----
     For "image" the collection banner takes the main colour of its art: the image is drawn at 32px, pixels are
     grouped by hue (vivid ones only) and the heaviest group's average becomes --bn-c (+ darker/lighter mixes). */
  function initBannerTone() {
    var mode = null;
    try {
      mode = new URLSearchParams(location.search).get('vn_banner');
      if (mode === 'reset') { sessionStorage.removeItem('vn_banner'); mode = null; }
      else if (mode === 'theme' || mode === 'image') sessionStorage.setItem('vn_banner', mode);
      else mode = sessionStorage.getItem('vn_banner');
    } catch (e) {}
    if (mode !== 'theme' && mode !== 'image') return;
    root.setAttribute('data-vn-bn', mode);
    if (mode !== 'image') return;
    document.querySelectorAll('.vn-coll__banner img[data-vn-tone-src]').forEach(function (img) {
      var banner = img.closest('.vn-coll__banner');
      var sample = function () {
        try {
          var c = document.createElement('canvas'); c.width = c.height = 32;
          var ctx = c.getContext('2d', { willReadFrequently: true });
          ctx.drawImage(img, 0, 0, 32, 32);
          var d = ctx.getImageData(0, 0, 32, 32).data, buckets = {};
          for (var i = 0; i < d.length; i += 4) {
            if (d[i + 3] < 200) continue;
            var r = d[i], g = d[i + 1], b = d[i + 2], mx = Math.max(r, g, b), mn = Math.min(r, g, b), l = (mx + mn) / 510;
            if (mx - mn < 60 || l < 0.15 || l > 0.85) continue;
            var h = mx === r ? ((g - b) / (mx - mn) + 6) % 6 : mx === g ? (b - r) / (mx - mn) + 2 : (r - g) / (mx - mn) + 4;
            var k = Math.floor(h * 2);
            var bk = buckets[k] || (buckets[k] = { n: 0, r: 0, g: 0, b: 0 });
            bk.n++; bk.r += r; bk.g += g; bk.b += b;
          }
          var best = null;
          Object.keys(buckets).forEach(function (k) { if (!best || buckets[k].n > best.n) best = buckets[k]; });
          if (!best || best.n < 20) return;
          var R = best.r / best.n, G = best.g / best.n, B = best.b / best.n;
          var mix = function (t, w) { return Math.round(R + (t - R) * w) + ' ' + Math.round(G + (t - G) * w) + ' ' + Math.round(B + (t - B) * w); };
          banner.style.setProperty('--bn-c', mix(0, 0.15));
          banner.style.setProperty('--bn-c-d', mix(0, 0.55));
          banner.style.setProperty('--bn-c-l', mix(255, 0.45));
        } catch (e) {}
      };
      if (img.complete && img.naturalWidth) sample(); else img.addEventListener('load', sample, { once: true });
    });
  }

  /* ---- Tabs (brand tabs on the homepage, PDP tabs) ---- */
  function initTabs() {
    document.querySelectorAll('[data-vn-tabs]').forEach(function (wrap) {
      if (wrap.dataset.vnBound) return;
      wrap.dataset.vnBound = '1';
      var tabs = wrap.querySelectorAll('[data-vn-tab]');
      var panels = wrap.querySelectorAll('[data-vn-panel]');

      function activate(id, focus) {
        tabs.forEach(function (tab) {
          var on = tab.getAttribute('data-vn-tab') === id;
          tab.setAttribute('aria-selected', String(on));
          tab.tabIndex = on ? 0 : -1;
          if (on && focus) tab.focus();
          if (on && tab.scrollIntoView && wrap.dataset.vnScrolled) {
            tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
          }
        });
        panels.forEach(function (panel) {
          var on = panel.getAttribute('data-vn-panel') === id;
          panel.hidden = !on;
          if (on) {
            panel.querySelectorAll('img[data-vn-src]').forEach(function (img) {
              img.src = img.getAttribute('data-vn-src');
              if (img.getAttribute('data-vn-srcset')) img.srcset = img.getAttribute('data-vn-srcset');
              img.removeAttribute('data-vn-src');
            });
            panel.querySelectorAll('[data-vn-reveal]').forEach(function (el) { el.classList.add('is-in'); });
          }
        });
        wrap.dataset.vnScrolled = '1';
      }

      tabs.forEach(function (tab, i) {
        tab.addEventListener('click', function () { activate(tab.getAttribute('data-vn-tab'), false); });
        tab.addEventListener('keydown', function (e) {
          var dir = e.key === 'ArrowLeft' ? 1 : e.key === 'ArrowRight' ? -1 : 0;
          if (!dir) return;
          e.preventDefault();
          var next = tabs[(i + dir + tabs.length) % tabs.length];
          activate(next.getAttribute('data-vn-tab'), true);
        });
      });
    });
  }

  /* ---- Horizontal scrollers with arrow buttons ---- */
  function initScrollers() {
    document.querySelectorAll('[data-vn-scroller]').forEach(function (wrap) {
      if (wrap.dataset.vnBound) return;
      wrap.dataset.vnBound = '1';
      var track = wrap.querySelector('[data-vn-track]');
      if (!track) return;
      // Hide the arrows when everything already fits.
      function sync() {
        wrap.classList.toggle('vn-fits', track.scrollWidth <= track.clientWidth + 4);
      }
      sync();
      if ('ResizeObserver' in window) new ResizeObserver(sync).observe(track);
      wrap.querySelectorAll('[data-vn-scroll]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var dir = btn.getAttribute('data-vn-scroll') === 'next' ? -1 : 1;
          if (getComputedStyle(track).direction === 'ltr') dir = -dir;
          track.scrollBy({ left: dir * track.clientWidth * 0.85, behavior: 'smooth' });
        });
      });
    });
  }

  /* ---- Tilt + spotlight on cards (pointer devices only) ---- */
  function initSpotlight() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    var last = null, queued = false;
    document.addEventListener('pointermove', function (e) {
      last = e;
      if (queued) return;
      queued = true;
      requestAnimationFrame(function () {
        queued = false;
        var card = last.target.closest && last.target.closest('[data-vn-spot]');
        if (!card) return;
        var r = card.getBoundingClientRect();
        card.style.setProperty('--mx', ((last.clientX - r.left) / r.width * 100) + '%');
        card.style.setProperty('--my', ((last.clientY - r.top) / r.height * 100) + '%');
      });
    }, { passive: true });
  }

  /* ---- Hero slider: autoplay 6s (pauses on hover/focus/hidden tab), dots, swipe, GTA countdown ---- */
  function initHeroSlider() {
    var root = document.querySelector('[data-vn-slider]');
    if (!root || root.dataset.vnBound) return;
    root.dataset.vnBound = '1';
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-vn-slide]'));
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-vn-dot]'));
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var MS = 6000;
    var cur = 0;
    var timer = 0;
    var paused = false;

    function show(i) {
      cur = (i + slides.length) % slides.length;
      slides.forEach(function (sl, k) {
        var on = k === cur;
        sl.classList.toggle('is-active', on);
        sl.setAttribute('aria-hidden', on ? 'false' : 'true');
        sl.querySelectorAll('a, button').forEach(function (el) { el.tabIndex = on ? 0 : -1; });
      });
      dots.forEach(function (d, k) {
        d.classList.remove('is-active');
        d.setAttribute('aria-selected', k === cur ? 'true' : 'false');
      });
      if (dots[cur]) { void dots[cur].offsetWidth; dots[cur].classList.add('is-active'); }
      schedule();
    }
    // Autoplay waits for the visitor's first scroll/tap/key (2026-10-15 speed pass): a slide change at 6s with no
    // input painted the big GTA image and became a late LCP. The dot progress is held until then.
    var armed = false;
    function arm() {
      if (armed) return;
      armed = true;
      ['scroll', 'pointerdown', 'keydown', 'touchstart', 'wheel'].forEach(function (ev) { window.removeEventListener(ev, arm); });
      root.classList.remove('is-waiting');
      if (!paused) show(cur);
    }
    function schedule() {
      clearTimeout(timer);
      if (slides.length < 2 || reduce || paused || !armed) return;
      timer = setTimeout(function () { show(cur + 1); }, MS);
    }
    function setPaused(p) {
      paused = p;
      root.classList.toggle('is-paused', p);
      if (p) clearTimeout(timer);
      else show(cur);
    }

    if (slides.length > 1) {
      root.style.setProperty('--vn-slide-ms', MS + 'ms');
      if (reduce) root.classList.add('is-static');
      dots.forEach(function (d) { d.addEventListener('click', function () { show(parseInt(d.getAttribute('data-vn-dot'), 10)); }); });
      root.addEventListener('mouseenter', function () { setPaused(true); });
      root.addEventListener('mouseleave', function () { setPaused(false); });
      root.addEventListener('focusin', function () { setPaused(true); });
      root.addEventListener('focusout', function (e) { if (!root.contains(e.relatedTarget)) setPaused(false); });
      document.addEventListener('visibilitychange', function () { if (document.hidden) clearTimeout(timer); else if (!paused) show(cur); });
      // swipe (RTL: swiping left goes to the previous slide)
      var x0 = null;
      root.addEventListener('touchstart', function (e) { x0 = e.touches[0].clientX; }, { passive: true });
      root.addEventListener('touchend', function (e) {
        if (x0 === null) return;
        var dx = e.changedTouches[0].clientX - x0;
        x0 = null;
        if (Math.abs(dx) > 40) show(cur + (dx > 0 ? 1 : -1));
      }, { passive: true });
      root.classList.add('is-waiting');
      ['scroll', 'pointerdown', 'keydown', 'touchstart', 'wheel'].forEach(function (ev) { window.addEventListener(ev, arm, { passive: true }); });
      show(0);
    }

    // countdowns (GTA slide)
    root.querySelectorAll('[data-date]').forEach(function (sl) {
      var end = new Date(sl.getAttribute('data-date')).getTime();
      var box = sl.querySelector('[data-vn-count]');
      var live = sl.querySelector('[data-vn-live]');
      var btn = sl.querySelector('[data-vn-btn]');
      var dateEl = sl.querySelector('[data-vn-date]');
      var u = {};
      ['d', 'h', 'm', 's'].forEach(function (k) { u[k] = sl.querySelector('[data-u="' + k + '"]'); });
      if (dateEl && !isNaN(end)) {
        try { dateEl.textContent = 'الإطلاق ' + new Date(end).toLocaleDateString('ar', { day: 'numeric', month: 'long', year: 'numeric', numberingSystem: 'latn', timeZone: 'Asia/Riyadh' }); } catch (e) {}
      }
      var pad = function (v) { return v < 10 ? '0' + v : String(v); };
      var tick = function () {
        var left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (isNaN(end) || left === 0) {
          if (box) box.hidden = true;
          if (live) live.hidden = false;
          if (btn) btn.textContent = 'اطلب الآن';
          return false;
        }
        u.d.textContent = Math.floor(left / 86400);
        u.h.textContent = pad(Math.floor(left % 86400 / 3600));
        u.m.textContent = pad(Math.floor(left % 3600 / 60));
        u.s.textContent = pad(left % 60);
        return true;
      };
      if (tick()) { var t = setInterval(function () { if (!tick()) clearInterval(t); }, 1000); }
    });
  }

  /* ---- Rows that move sideways: load their images once they near the screen ---- */
  function initEagerRows() {
    var rows = document.querySelectorAll('[data-vn-eager-row]:not([data-vn-bound])');
    if (!rows.length) return;
    function load(row) {
      row.querySelectorAll('img[loading="lazy"]').forEach(function (img) { img.loading = 'eager'; });
    }
    if (!('IntersectionObserver' in window)) { rows.forEach(load); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        load(entry.target);
        io.unobserve(entry.target);
      });
    }, { rootMargin: '400px 0px' });
    rows.forEach(function (row) { row.setAttribute('data-vn-bound', ''); io.observe(row); });
  }

  /* ---- Offer countdown (end time in seconds, from the theme setting) ---- */
  function initCountdowns() {
    document.querySelectorAll('[data-vn-countdown]').forEach(function (box) {
      if (box.dataset.vnBound) return;
      box.dataset.vnBound = '1';
      var end = parseInt(box.getAttribute('data-vn-countdown'), 10) * 1000;
      var parts = { d: box.querySelector('[data-d]'), h: box.querySelector('[data-h]'), m: box.querySelector('[data-m]'), s: box.querySelector('[data-s]') };
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      function tick() {
        var left = Math.max(0, Math.floor((end - Date.now()) / 1000));
        if (!left) { box.hidden = true; return; }
        parts.d.textContent = pad(Math.floor(left / 86400));
        parts.h.textContent = pad(Math.floor(left % 86400 / 3600));
        parts.m.textContent = pad(Math.floor(left % 3600 / 60));
        parts.s.textContent = pad(left % 60);
        window.setTimeout(tick, 1000);
      }
      tick();
    });
  }

  /* ---- Product page: floating buy bar + collapsible panels ---- */
  function initProductExtras() {
    var main = document.querySelector('[data-vn-main-form]') || document.getElementById('vs-product-form');
    var float = document.querySelector('[data-vn-float]');
    var anchor = document.querySelector('[data-vn-buy-anchor]');

    if (float && anchor && !float.dataset.vnBound) {
      float.dataset.vnBound = '1';
      float.hidden = false;
      var io = new IntersectionObserver(function (entries) {
        var e = entries[0];
        // Show only once the buttons have scrolled up past the top, not before reaching them.
        var past = !e.isIntersecting && e.boundingClientRect.top < 0;
        float.classList.toggle('is-on', past);
        document.documentElement.classList.toggle('vn-float-on', past);
      });
      io.observe(anchor);

      var floatId = float.querySelector('[data-vn-float-id]');
      if (main && floatId) {
        main.addEventListener('change', function () {
          window.setTimeout(function () {
            var id = main.querySelector('[name="id"]');
            if (id) floatId.value = id.value;
          }, 0);
        });
      }
      var nowBtn = float.querySelector('[data-vn-float-now]');
      if (nowBtn) {
        nowBtn.addEventListener('click', function () {
          var buy = main && main.querySelector('.vs-product-info__btn-buy');
          if (buy) buy.click();
        });
      }
      var cartBtn = float.querySelector('[data-vn-float-cart]');
      if (cartBtn) {
        cartBtn.addEventListener('click', function () {
          var atc = main && main.querySelector('.vs-product-info__btn-atc');
          if (atc) atc.click();
        });
      }
    }

    document.querySelectorAll('[data-vn-open]').forEach(function (btn) {
      if (btn.dataset.vnBound) return;
      btn.dataset.vnBound = '1';
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-vn-open') === 'reviews' ? 'activation' : btn.getAttribute('data-vn-open');
        var tab = document.querySelector('.vs-product-tabs__tab[data-tab="' + key + '"]');
        if (tab) tab.click();
        var wrap = document.querySelector('.vs-product-tabs');
        if (wrap) wrap.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    });
  }

  /* ---- Express checkout: show it only when Shopify offers Apple Pay or Google Pay ----
     Shopify's button renders the buyer's "recommended" wallet. Shop Pay (or the plain
     "buy it now" fallback, which duplicates our own button) keeps the box hidden. */
  var VN_WALLETS = ['apple_pay', 'google_pay'];
  function walletOf(box) {
    var el = box.querySelector('shopify-accelerated-checkout');
    if (!el) return '';
    try { return (JSON.parse(el.getAttribute('recommended') || '{}').name || ''); } catch (e) { return ''; }
  }
  function swapWallet(box, params) {
    var orig = box.querySelector('shopify-accelerated-checkout');
    if (!orig) return false;
    var el = document.createElement('shopify-accelerated-checkout');
    for (var i = 0; i < orig.attributes.length; i++) el.setAttribute(orig.attributes[i].name, orig.attributes[i].value);
    el.setAttribute('recommended', JSON.stringify(params));
    orig.parentNode.replaceChild(el, orig);
    return true;
  }

  function showWallets(boxes) {
    var any = false;
    boxes.forEach(function (box) {
      var w = walletOf(box);
      var ok = VN_WALLETS.indexOf(w) !== -1;
      box.hidden = !ok;
      box.setAttribute('data-vn-wallet', w);
      if (ok) any = true;
    });
    document.documentElement.classList.toggle('vn-has-applepay', any);
  }

  function initExpressWallets() {
    var boxes = Array.prototype.slice.call(document.querySelectorAll('[data-vn-applepay]'));
    if (!boxes.length) return;
    showWallets(boxes);

    // Desktop browsers without native Apple Pay: load Apple's SDK (QR code flow, iOS 18+)
    // and switch the button to Apple Pay.
    var paramsEl = document.querySelector('[data-vn-applepay-params]');
    var needsQr = boxes.some(function (box) { return VN_WALLETS.indexOf(walletOf(box)) === -1; });
    var desktop = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!paramsEl || !needsQr || !desktop || window.ApplePaySession) return;
    var params;
    try { params = JSON.parse(paramsEl.textContent); } catch (e) { return; }

    var s = document.createElement('script');
    s.src = 'https://applepay.cdn-apple.com/jsapi/1.latest/apple-pay-sdk.js';
    s.crossOrigin = 'anonymous';
    s.async = true;
    s.onload = function () {
      var can = false;
      try { can = !!(window.ApplePaySession && window.ApplePaySession.canMakePayments()); } catch (e) {}
      if (!can) return;
      boxes.forEach(function (box) {
        if (VN_WALLETS.indexOf(walletOf(box)) === -1) swapWallet(box, params);
      });
      showWallets(boxes);
    };
    document.head.appendChild(s);
  }

  /* ---- Share button: native share sheet on phones, otherwise copy the link and say so ---- */
  function vnToast(text) {
    var el = document.querySelector('.vn-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'vn-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.textContent = text;
    el.classList.add('is-on');
    window.clearTimeout(vnToast.t);
    vnToast.t = window.setTimeout(function () { el.classList.remove('is-on'); }, 2200);
  }

  function initShare() {
    document.querySelectorAll('[data-vn-share]').forEach(function (btn) {
      if (btn.dataset.vnBound) return;
      btn.dataset.vnBound = '1';
      btn.addEventListener('click', function () {
        var url = location.origin + location.pathname;
        var title = document.title;
        if (navigator.share) {
          navigator.share({ title: title, url: url }).catch(function () {});
          return;
        }
        // Synchronous copy inside the click works without clipboard permission prompts.
        var ok = false;
        var ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.cssText = 'position:fixed;top:-1000px;opacity:0';
        document.body.appendChild(ta);
        ta.select();
        try { ok = document.execCommand('copy'); } catch (e) {}
        document.body.removeChild(ta);
        if (!ok && navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(url).catch(function () {});
          ok = true;
        }
        if (ok) vnToast('✓ تم نسخ رابط المنتج');
        else window.prompt('انسخ الرابط:', url);
      });
    });
  }

  /* ---- Bundle switch: keep aria-checked in step with the checkbox (visual/a11y only) ---- */
  function initDealSwitch() {
    document.querySelectorAll('[data-vs-bundle-toggle][role="switch"]').forEach(function (input) {
      if (input.dataset.vnBound) return;
      input.dataset.vnBound = '1';
      var sync = function () { input.setAttribute('aria-checked', String(input.checked)); };
      input.addEventListener('change', sync);
      sync();
    });
  }

  /* ---- Collection page: subcategory cards filter the grid in place (?sub=handle keeps the pick on reload) ---- */
  function initSubFilter() {
    var bar = document.querySelector('[data-vn-subbar]');
    if (!bar || bar.dataset.vnBound) return;
    bar.dataset.vnBound = '1';
    var cards = Array.prototype.slice.call(document.querySelectorAll('#vn-coll-products [data-vs-product-card]'));
    var btns = Array.prototype.slice.call(bar.querySelectorAll('[data-vn-sub]'));
    var has = function (card, h) { return (card.getAttribute('data-vn-subs') || '').indexOf(',' + h + ',') !== -1; };
    var pick = function (h) {
      btns.forEach(function (b) {
        var on = b.getAttribute('data-vn-sub') === h;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-pressed', String(on));
      });
      cards.forEach(function (c) { c.classList.toggle('is-sub-off', !!h && !has(c, h)); });
      try {
        var u = new URL(location.href);
        if (h) u.searchParams.set('sub', h); else u.searchParams.delete('sub');
        history.replaceState(history.state, '', u);
      } catch (e) {}
    };
    bar.addEventListener('click', function (e) {
      var b = e.target.closest('[data-vn-sub]');
      if (b) pick(b.getAttribute('data-vn-sub'));
    });
    var start = new URLSearchParams(location.search).get('sub');
    var startBtn = start && btns.filter(function (b) { return b.getAttribute('data-vn-sub') === start; })[0];
    if (startBtn) {
      pick(start);
      var br = bar.getBoundingClientRect(), sr = startBtn.getBoundingClientRect();
      bar.scrollLeft += sr.left - br.left - (br.width - sr.width) / 2;
    }
  }

  /* ---- Collection page: the long category description (.vs-coll-desc, a custom-liquid section of the collection template)
     starts closed, a few lines with a fade, and opens with «اقرأ المزيد» (2026-10-19, the user's request; the text stays in the page for SEO) ---- */
  function initCollDesc() {
    document.querySelectorAll('.vs-coll-desc').forEach(function (d) {
      if (d.dataset.vnBound) return;
      d.dataset.vnBound = '1';
      if (d.scrollHeight < 260) return;
      d.classList.add('is-collapsed');
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'vs-coll-desc__more';
      b.setAttribute('aria-expanded', 'false');
      b.innerHTML = '<span>اقرأ المزيد</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6"/></svg>';
      d.insertAdjacentElement('afterend', b);
      b.addEventListener('click', function () {
        var open = d.classList.toggle('is-collapsed') === false;
        b.setAttribute('aria-expanded', String(open));
        b.firstChild.textContent = open ? 'إخفاء' : 'اقرأ المزيد';
        if (!open) d.scrollIntoView({ block: 'nearest' });
      });
    });
  }

  function init() {
    initSubFilter();
    initCollDesc();
    initDealSwitch();
    initShare();
    initExpressWallets();
    initProductExtras();
    initCountdowns();
    initHeroSlider();
    initEagerRows();
    initThemeToggle();
    initBannerTone();
    initHeader();
    initProHeader();
    initAnnounce();
    initDrawer();
    // one frame later: the rect reads in initAutoReveal no longer force a style recalc while the script evaluates
    requestAnimationFrame(function () { initAutoReveal(); initReveal(); initCountUp(); });
    initTabs();
    initScrollers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  initSpotlight();
  document.addEventListener('shopify:section:load', init);
  window.VisionNeo = { init: init, setTheme: setTheme };
})();
